import React, { useState, useCallback } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { IconXmark } from '../../icons';
import IconButton from '../core/IconButton';
import { DialogWindow } from '../DialogWindow';
import { useSnackbar } from 'notistack';
import InstantUploadDropper from './InstantUploadDropper';
import { useRebusMutations } from '../../hooks/useRebus';

interface ImportRebusDataDialogProps {
  open: boolean;
  onClose: () => void;
}

const messages = defineMessages({
  fileUploaded: {
    id: 'import_rebus_Data.file_uploaded',
    defaultMessage:
      'File uploaded. You will be notified by email once the import process has concluded.',
  },
  dialogTitle: {
    id: 'import_rebus_data_dialog.import_rebus_data',
    defaultMessage: 'Import rebus data',
  },
  dismiss: {
    id: 'import_rebus_data_dialog.dismiss',
    defaultMessage: 'Dismiss',
  },
});

const ImportRebusDataDialog: React.FC<ImportRebusDataDialogProps> = ({
  open,
  onClose,
}) => {
  const intl = useIntl();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const {
    upload: { mutateAsync: upload },
  } = useRebusMutations();

  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback(
    (files: File[]) => {
      setUploading(true);
      void upload(files[0])
        .then(() => {
          setUploading(false);
          const action = (key: string | number): React.ReactNode => {
            return (
              <IconButton
                variant="ghost"
                aria-label={intl.formatMessage(messages.dismiss)}
                onClick={() => closeSnackbar(key)}
                icon={<IconXmark />}
              />
            );
          };
          enqueueSnackbar(intl.formatMessage(messages.fileUploaded), {
            autoHideDuration: 10000,
            action,
          });
          onClose();
        })
        .catch(() => onClose());
    },
    [onClose, upload, enqueueSnackbar, closeSnackbar, intl, setUploading],
  );

  return (
    <DialogWindow
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      title={intl.formatMessage(messages.dialogTitle)}
      content={
        <div className="mt-4">
          <InstantUploadDropper
            disabled={uploading}
            dropzoneOptions={{
              onDrop,
              maxFiles: 1,
              accept: [
                'application/zip',
                'application/x-zip',
                'application/x-zip-compressed',
                'application/octet-stream',
              ],
            }}
          />
        </div>
      }
    />
  );
};

export default ImportRebusDataDialog;
